import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { useBranding } from "../lib/branding";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const BG_URL =
    "https://images.unsplash.com/photo-1515263487990-61b07816b324?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjByZXNpZGVudGlhbCUyMGFwYXJ0bWVudCUyMGJ1aWxkaW5nJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzc5ODg2ODE2fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
    const { login } = useAuth();
    const brand = useBranding();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await login(email, password);
            toast.success("Welcome back");
            navigate("/", { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen grid md:grid-cols-2">
            {/* Left: image */}
            <div
                className="hidden md:block relative"
                style={{
                    backgroundImage: `url('${BG_URL}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="absolute inset-0 bg-slate-50/90" />
                <div className="relative h-full flex flex-col justify-between p-10">
                    <div className="font-heading text-xl font-bold text-slate-900">
                        {brand.product_name}
                    </div>
                    <div className="max-w-sm">
                        <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">
                            Calm. Secure. Practical.
                        </div>
                        <p className="text-sm text-slate-700 leading-relaxed">
                            Manage estate cases, share documents safely with strict privacy controls, and draft
                            evidence-backed parking ticket appeals — all in one place.
                        </p>
                    </div>
                </div>
            </div>

            {/* Right: form */}
            <div className="flex items-center justify-center px-6 py-12 bg-white">
                <div className="w-full max-w-sm">
                    <div className="md:hidden font-heading text-lg font-bold text-slate-900 mb-1">
                        {brand.product_name}
                    </div>
                    <h1 className="font-heading text-2xl font-bold text-slate-900">Sign in</h1>
                    <p className="text-sm text-slate-500 mt-1 mb-6">
                        Access your estate dashboard and case files.
                    </p>

                    <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
                        <div className="space-y-1.5">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@estate.example.com"
                                required
                                data-testid="login-email-input"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Your password"
                                required
                                data-testid="login-password-input"
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-slate-700 hover:bg-slate-800 text-white"
                            data-testid="login-submit-button"
                        >
                            {submitting ? "Signing in…" : "Sign in"}
                        </Button>
                    </form>

                    <div className="text-xs text-slate-500 mt-4" data-testid="login-reset-hint">
                        Forgotten your password? There's no self-serve reset yet — ask your estate
                        admin to help you back in.
                    </div>

                    <div className="text-xs text-slate-500 mt-6 space-y-1.5">
                        <p>
                            New here?{" "}
                            <Link to="/signup" className="text-slate-800 underline" data-testid="signup-link">
                                Start a private workspace
                            </Link>{" "}
                            — free, just your own documents.
                        </p>
                        <p>
                            Got a join code?{" "}
                            <Link to="/join" className="text-slate-800 underline" data-testid="join-link">
                                Join your estate
                            </Link>
                        </p>
                        <p>
                            Been invited by email?{" "}
                            <Link to="/register" className="text-slate-800 underline" data-testid="register-link">
                                Accept your invitation
                            </Link>
                        </p>
                    </div>

                </div>
            </div>
        </div>
    );
}
